import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { WhatsAppConnectionResponse } from "@multi-chat/shared";
import { getConnection } from "../../api/whatsapp-api.js";
import { useOrganizations } from "../organizations/organization-context.js";
import { nextInboxWorkspaceScope, type InboxWorkspaceScope } from "./whatsapp-workspace-scope.js";

interface WhatsAppWorkspacePhoneContextValue {
  /** Increments when the selected workspace endpoint changes; drives inbox rescoping. */
  inboxScopeVersion: number;
  selectedWorkspaceEndpointId: number | null;
  connection: WhatsAppConnectionResponse | null;
  applyConnection: (connection: WhatsAppConnectionResponse) => void;
  refreshConnection: () => void;
}

const WhatsAppWorkspacePhoneContext = createContext<WhatsAppWorkspacePhoneContextValue | null>(
  null,
);

export function WhatsAppWorkspacePhoneProvider({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const { selected } = useOrganizations();
  const organizationId = selected?.id ?? null;
  const [connection, setConnection] = useState<WhatsAppConnectionResponse | null>(null);
  const [inboxScopeVersion, setInboxScopeVersion] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const scopeRef = useRef<InboxWorkspaceScope | null>(null);
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;

  const recordScope = useCallback((scopeOrganizationId: number, endpointId: number | null) => {
    const { nextScope, shouldBump } = nextInboxWorkspaceScope(
      scopeRef.current,
      scopeOrganizationId,
      endpointId,
    );
    scopeRef.current = nextScope;
    if (shouldBump) {
      setInboxScopeVersion((version) => version + 1);
    }
  }, []);

  const applyConnection = useCallback(
    (next: WhatsAppConnectionResponse) => {
      const currentOrganizationId = organizationIdRef.current;
      if (currentOrganizationId == null) return;
      setConnection(next);
      recordScope(currentOrganizationId, next.selectedWorkspaceEndpointId ?? null);
    },
    [recordScope],
  );

  const refreshConnection = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (organizationId == null) {
      scopeRef.current = null;
      setConnection(null);
      setInboxScopeVersion(0);
      return;
    }

    const controller = new AbortController();
    void (async () => {
      try {
        const next = await getConnection(organizationId, controller.signal);
        if (controller.signal.aborted) return;
        setConnection(next);
        recordScope(organizationId, next.selectedWorkspaceEndpointId ?? null);
      } catch {
        if (controller.signal.aborted) return;
        setConnection(null);
        recordScope(organizationId, null);
      }
    })();

    return () => controller.abort();
  }, [organizationId, reloadToken, recordScope]);

  const value = useMemo(
    (): WhatsAppWorkspacePhoneContextValue => ({
      inboxScopeVersion,
      selectedWorkspaceEndpointId: connection?.selectedWorkspaceEndpointId ?? null,
      connection,
      applyConnection,
      refreshConnection,
    }),
    [applyConnection, connection, inboxScopeVersion, refreshConnection],
  );

  return (
    <WhatsAppWorkspacePhoneContext.Provider value={value}>
      {children}
    </WhatsAppWorkspacePhoneContext.Provider>
  );
}

export function useWhatsAppWorkspacePhone(): WhatsAppWorkspacePhoneContextValue {
  const ctx = useContext(WhatsAppWorkspacePhoneContext);
  if (!ctx) {
    throw new Error("useWhatsAppWorkspacePhone must be used within WhatsAppWorkspacePhoneProvider");
  }
  return ctx;
}
